// §4.2 Pricing tab — the PRICE CHECK panel: §3.2's engine as a dry run
// (Module 09, P3-A part 2; the §4.2 sentence naming this panel is the spec
// write-back shipped with this slice). Until P3-B's quote composer exists,
// this panel is the surface M09-S01/S02/S15 drive through §3.2 — the same
// resolve endpoint the composer will call, exercised pre-composer.
//
// Inputs: product by EXACT SKU (the remnants itemMatch idiom), quantity
// (exact decimal text, never a float), UoM (defaulting to the matched
// product's base UoM — the §4.3 base-UoM override write-back's display side),
// wanted date. POST /v1/pricing/resolve is a WRITE-NOTHING dry run
// (useMutation, no invalidation).
//
// The output card is HONEST TO THE WIRE (§7 omission redaction):
//   * margin/cost keys are OMITTED without `sales.margin.read` — the margin
//     block renders ONLY when they are present; without them the always-
//     present `margin_breached` boolean renders as a neutral "Needs approval"
//     chip and nothing else (§4.4's rule);
//   * `distributor_fallback: true` renders the §9.5 "b2b (not in eligible
//     list)" chip with `data-fallback="true"` — a fact, never an error;
//   * 422 PRICE_NOT_LISTED renders as the "no list price — manual price
//     needed" NOTE — §9.13's refusal is a fact about the price book, not a
//     failure of this screen, so it does not wear the error register.
//
// THE ANSWER IS PRICES (D-301 round 3). The server refuses the dry run to a
// reader without `sales.read_value` (403 naming the grant), and the §4.2 tab
// is open to planner, who does not hold it. A reader the role mirror KNOWS is
// refused is never offered the form; a reader it cannot see into (a custom
// role) may ask, and the server's refusal replaces the form with the same
// sentence rather than an error card.

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { Field } from "../../../../components/field";
import { Select } from "../../../../components/select";
import { StatusChip } from "../../../../components/status-chip";
import { RequestError } from "../../../../lib/request";
import { facilityTodayIso } from "../../../../lib/business-dates";
import { useTenantTimezone } from "../../../../lib/role-codes";
import { useUoms } from "../../catalogue/hooks/catalogue-queries";
import { useProductSearch } from "../../production/hooks/production-queries";
import { formatMinor } from "../lib/money";
import { MutationFailureNote } from "../mutation-failure-note";
import { useResolvePrice, type Customer } from "../hooks/sales-queries";
import { useSalesPermissions } from "../hooks/permissions";
import { isSalesValueRefusal, valueSortVerdict } from "../lib/value-order";

const QTY = /^(?=.*[1-9])\d{1,11}(\.\d{1,3})?$/u;

export function PriceCheck({ customer }: { customer: Customer }) {
  const { t, i18n } = useTranslation();
  const [skuText, setSkuText] = useState("");
  const [qty, setQty] = useState("");
  const [uomId, setUomId] = useState("");
  // The wanted date DEFAULTS to the TENANT's today, not the browser's — the
  // same §9.14 rule the §4.4 composer follows and the engine prices by. A
  // browser default is one day off for part of every day at a real distance
  // from the plant, and this field feeds `wanted_date`, which SELECTS the §2.7
  // list window and the §2.9 override window (D-089).
  //
  // Held as an OVERRIDE rather than seeded state: `useState(() => …)` runs its
  // initialiser once, so a zone that arrives after mount — the session feed is
  // in flight on a cold load — would never reach a seeded value. Deriving at
  // render instead means the default corrects itself the moment the zone lands
  // and stops moving the instant the user picks a date, with no effect and no
  // "has the user touched it" flag to get wrong.
  const [dateOverride, setDateOverride] = useState<string | undefined>(undefined);
  const tenantTimezone = useTenantTimezone();
  const date = dateOverride ?? facilityTodayIso(Date.now(), tenantTimezone);

  const uoms = useUoms();
  const search = useProductSearch(skuText.trim());
  const match = useMemo(() => {
    const needle = skuText.trim().toLowerCase();
    if (needle.length === 0) return undefined;
    return (search.data?.items ?? []).find((p) => p.sku.toLowerCase() === needle);
  }, [search.data, skuText]);

  const resolve = useResolvePrice();
  const perms = useSalesPermissions();

  if (valueSortVerdict(perms) === "refused" || isSalesValueRefusal(resolve.error)) {
    return (
      <Card
        className="sales-price-check"
        data-testid="sales-price-check"
        title={t("sales.priceCheck.title")}
      >
        <p className="sales-hint" data-testid="price-check-value-hidden">
          {t("sales.priceCheck.valueHidden")}
        </p>
      </Card>
    );
  }

  const qtyValid = QTY.test(qty.trim());
  // The UoM defaults to the matched product's BASE unit; a picked value wins.
  const effectiveUomId = uomId.length > 0 ? uomId : (match?.base_uom_id ?? "");

  const notListed =
    resolve.error instanceof RequestError && resolve.error.code === "PRICE_NOT_LISTED";
  const result = resolve.data;
  const marginPresent =
    result !== undefined &&
    (result.margin_pct !== undefined || result.unit_cost_minor !== undefined);

  return (
    <Card
      className="sales-price-check"
      data-testid="sales-price-check"
      title={t("sales.priceCheck.title")}
    >
      <div className="filter-bar sales-price-check-inputs">
        <Field label={t("sales.priceCheck.product")} wide>
          <input
            value={skuText}
            placeholder={t("sales.priceCheck.productHint")}
            data-testid="price-check-product"
            onChange={(e) => {
              setSkuText(e.target.value);
            }}
          />
          {skuText.trim().length > 0 ? (
            match !== undefined ? (
              <span className="sales-applied" data-testid="price-check-product-match">
                {t("sales.priceCheck.productApplied", { sku: match.sku, name: match.name })}
              </span>
            ) : (
              <span className="sales-applied" data-testid="price-check-product-pending">
                {t("sales.priceCheck.productNoMatch")}
              </span>
            )
          ) : null}
        </Field>
        <Field label={t("sales.priceCheck.qty")}>
          <input
            inputMode="decimal"
            value={qty}
            data-testid="price-check-qty"
            onChange={(e) => {
              setQty(e.target.value);
            }}
          />
        </Field>
        <Field label={t("sales.priceCheck.uom")}>
          <Select
            value={effectiveUomId}
            data-testid="price-check-uom"
            onChange={(e) => {
              setUomId(e.target.value);
            }}
          >
            {match === undefined && effectiveUomId.length === 0 ? (
              <option value="">{t("sales.priceCheck.uomPick")}</option>
            ) : null}
            {(uoms.data?.items ?? []).map((uom) => (
              <option key={uom.id} value={uom.id}>
                {uom.code}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("sales.priceCheck.date")}>
          <input
            type="date"
            value={date}
            data-testid="price-check-date"
            onChange={(e) => {
              setDateOverride(e.target.value);
            }}
          />
        </Field>
        <Button
          variant="primary"
          size="sm"
          data-testid="price-check-resolve"
          busy={resolve.isPending}
          busyLabel={t("sales.priceCheck.resolving")}
          disabled={match === undefined || !qtyValid || effectiveUomId.length === 0}
          onClick={() => {
            if (match === undefined) return;
            resolve.mutate({
              customer_id: customer.id,
              product_id: match.id,
              qty: qty.trim(),
              uom_id: effectiveUomId,
              wanted_date: date,
            });
          }}
        >
          {t("sales.priceCheck.resolve")}
        </Button>
      </div>

      {notListed ? (
        // §9.13: a FACT about the price book, not an error card.
        <p className="sales-hint" data-testid="price-check-not-listed">
          {t("sales.priceCheck.notListed")}
        </p>
      ) : resolve.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.priceCheck.failed")}
          error={resolve.error}
        />
      ) : null}

      {result !== undefined ? (
        <div className="sales-price-result" data-testid="price-check-result">
          <span className="sales-price-main">
            <span className="sales-price-label">{t("sales.priceCheck.price")}</span>
            <strong className="num" data-testid="price-check-price">
              {formatMinor(result.unit_price_minor, result.currency, i18n.language)}
            </strong>
          </span>
          <span className="sales-price-main">
            <span className="sales-price-label">{t("sales.priceCheck.listPrice")}</span>
            <span className="num" data-testid="price-check-list-price">
              {formatMinor(result.list_price_minor, result.currency, i18n.language)}
            </span>
          </span>
          <StatusChip
            variant={result.distributor_fallback ? "warn" : "accent"}
            dot={false}
            data-testid="price-check-source"
            data-source={result.price_source}
            data-fallback={result.distributor_fallback ? "true" : undefined}
          >
            {result.distributor_fallback
              ? t("sales.priceCheck.fallbackChip")
              : t(`sales.priceCheck.source.${result.price_source}`)}
          </StatusChip>

          {result.margin_breached ? (
            <StatusChip
              variant={marginPresent ? "bad" : "neutral"}
              data-testid="price-check-breach"
            >
              {t("sales.priceCheck.needsApproval")}
            </StatusChip>
          ) : null}

          {marginPresent ? (
            <span className="sales-price-margin" data-testid="price-check-margin">
              {result.margin_pct !== undefined ? (
                <span className="num">
                  {t("sales.priceCheck.marginPct", { pct: result.margin_pct })}
                </span>
              ) : null}
              {result.unit_cost_minor !== undefined ? (
                <span className="num">
                  {t("sales.priceCheck.unitCost", {
                    cost: formatMinor(result.unit_cost_minor, result.currency, i18n.language),
                  })}
                </span>
              ) : null}
              {result.margin_guard_pct !== undefined ? (
                <span className="num">
                  {t("sales.priceCheck.guardPct", { pct: result.margin_guard_pct })}
                </span>
              ) : null}
            </span>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}
